import React from 'react';
import { Crown, DollarSign, Zap, Users, Award, Shield } from 'lucide-react';

const BenefitsSection = () => {
  const benefits = [
    {
      icon: Crown,
      title: "Founding Member Badge",
      description: "Exclusive lifetime badge on your profile showcasing your founding member status",
      color: "from-purple-500 to-purple-600",
      highlight: "Exclusive"
    },
    {
      icon: DollarSign,
      title: "$50 Service Credit",
      description: "Receive $50 credit on your first ProConnect service booking - our gift to founding members",
      color: "from-green-500 to-green-600",
      highlight: "$50 Value"
    },
    {
      icon: Zap,
      title: "Priority Service",
      description: "Skip the line with priority booking and faster response times from service providers",
      color: "from-orange-500 to-orange-600",
      highlight: "VIP Access"
    },
    {
      icon: Shield,
      title: "Lifetime App Access",
      description: "Free access to the ProConnect mobile app forever - while others pay booking fees",
      color: "from-blue-500 to-blue-600",
      highlight: "No Fees"
    },
    {
      icon: Users,
      title: "Wall of Founders",
      description: "Optional feature on our Wall of Founders page - showcase your business or profile",
      color: "from-indigo-500 to-indigo-600",
      highlight: "Recognition"
    },
    {
      icon: Award,
      title: "Early Access Features",
      description: "Be first to access new platform features and provide feedback that shapes ProConnect",
      color: "from-teal-500 to-teal-600",
      highlight: "First Access"
    }
  ];

  return (
    <section id="benefits-section" className="py-24 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-100/30 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-orange-100/30 to-transparent rounded-full blur-3xl"></div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-6 py-3 text-sm font-semibold mb-8">
            <Crown className="w-4 h-4" />
            Founding Member Exclusive Benefits
          </div>
          
          <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-8 leading-tight">
            Your <span className="text-transparent bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text">Founding Member</span> Perks
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            These exclusive benefits are only available to our first 1,000 members. 
            Once registration closes, these perks will never be offered again.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {benefits?.map((benefit, index) => {
            const IconComponent = benefit?.icon;
            return (
              <div 
                key={index}
                className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 relative overflow-hidden"
              >
                {/* Highlight Badge */}
                <div className="absolute top-6 right-6">
                  <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {benefit?.highlight}
                  </span>
                </div>
                {/* Icon */}
                <div className={`w-16 h-16 bg-gradient-to-br ${benefit?.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors duration-300">
                  {benefit?.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit?.description}
                </p>
                {/* Hover Effect Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"></div>
              </div>
            );
          })}
        </div>

        {/* Value Summary */}
        <div className="bg-gradient-to-r from-primary to-blue-700 rounded-3xl p-8 md:p-12 text-center text-white">
          <h3 className="text-3xl md:text-4xl font-bold mb-4">
            Total Founding Member Value: <span className="text-orange-300">$500+</span>
          </h3>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Between the service credit, free app access, priority service, and exclusive recognition - 
            founding members receive over $500 in lifetime value.
          </p>
          
          <button 
            className="bg-white text-primary font-bold text-lg px-10 py-4 rounded-2xl hover:bg-gray-50 transition-all duration-300 transform hover:scale-105 shadow-lg"
            onClick={() => document.getElementById('waitlist-section')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Claim Your Founding Spot Now
          </button>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;